/**
 * Utility functions for routing with company slug support
 */

export const isSuperAdminUser = () => {
  if (typeof window === 'undefined') return false;
  try {
    const profile = JSON.parse(localStorage.getItem('profile') || '{}');
    return profile?.result?.role === 'superadmin';
  } catch {
    return false;
  }
};

/**
 * Get company slug from current URL or localStorage
 * @param {string} pathname - Optional pathname from useLocation (defaults to window.location.pathname)
 * @returns {string|null} Company slug or null
 */
export const getCompanySlugFromUrl = (pathname = null) => {
  // Use provided pathname or fallback to window.location.pathname
  const currentPath = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  
  // Try to get from URL first
  const pathMatch = currentPath.match(/^\/([^/]+)\/dashboard/);
  if (pathMatch && pathMatch[1]) {
    return pathMatch[1];
  }

  // Super admin koristi /dashboard/* rute — ne vući slug iz localStorage
  if (isSuperAdminUser()) {
    return null;
  }
  
  // Fallback to localStorage
  return localStorage.getItem('currentCompanySlug');
};

/**
 * Generate route path with company slug prefix if available
 * @param {string} path - Route path (e.g., '/jobs', '/clients')
 * @param {string} pathname - Optional pathname from useLocation (defaults to window.location.pathname)
 * @returns {string} Full path with slug prefix if available
 */
export const getRoutePath = (path, pathname = null) => {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;

  if (isSuperAdminUser()) {
    return `/dashboard/${cleanPath}`;
  }
  
  // Get company slug (use provided pathname or current location)
  const companySlug = getCompanySlugFromUrl(pathname);
  
  if (companySlug) {
    // If we're in a tenant route, use slug prefix
    return `/${companySlug}/dashboard/${cleanPath}`;
  }
  
  // Legacy route without slug
  return `/dashboard/${cleanPath}`;
};

/**
 * Generate route path for dashboard home
 * @param {string} pathname - Optional pathname from useLocation (defaults to window.location.pathname)
 * @returns {string} Dashboard home path
 */
export const getDashboardHomePath = (pathname = null) => {
  const companySlug = getCompanySlugFromUrl(pathname);
  if (companySlug) {
    return `/${companySlug}/dashboard`;
  }
  return '/dashboard';
};

/**
 * Landing after login: Poslovi (kalendar + zakazani / nezakazani / prosleđeni).
 * Superadmin ide na svoj dashboard.
 */
export const getJobsLandingPath = (slugOverride = null) => {
  if (isSuperAdminUser()) {
    return '/dashboard/superadmin';
  }
  const companySlug =
    slugOverride ||
    getCompanySlugFromUrl() ||
    (typeof window !== 'undefined' ? localStorage.getItem('currentCompanySlug') : null);
  if (companySlug) {
    return `/${companySlug}/dashboard/jobs`;
  }
  return '/dashboard/jobs';
};

/**
 * Check if current route is a tenant route (has slug)
 * @param {string} pathname - Optional pathname from useLocation (defaults to window.location.pathname)
 * @returns {boolean} True if tenant route
 */
export const isTenantRoute = (pathname = null) => {
  return !!getCompanySlugFromUrl(pathname);
};
